<template>
  <v-navigation-drawer
    :value="value"
    app
    clipped
    dark
    floating
    :width="isCollapsed && !isMobile ? 72 : 256"
    :mobile-breakpoint="960"
    class="app-sidebar"
    :class="{ 'app-sidebar--collapsed': isCollapsed && !isMobile }"
    :style="{ '--sidebar-background': background }"
    @input="$emit('input', $event)"
  >
    <template v-slot:prepend>
      <div
        class="app-sidebar__toolbar"
        :class="{ 'app-sidebar__toolbar--collapsed': isCollapsed && !isMobile }"
      >
        <v-tooltip right content-class="app-sidebar-tooltip" :open-delay="300" transition="fade-transition">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-sidebar__toggle"
              :ripple="false"
              :aria-label="isMobile ? 'Cerrar menú' : isCollapsed ? 'Expandir menú' : 'Plegar menú'"
              :aria-expanded="String(!isCollapsed || isMobile)"
              v-on="on"
              @click="togglePanel"
            >
              <span class="app-sidebar__toggle-icon">
                <v-icon size="16">mdi-chevron-right</v-icon>
              </span>
            </v-btn>
          </template>
          <span>{{ isMobile ? 'Cerrar menú' : isCollapsed ? 'Expandir menú' : 'Plegar menú' }}</span>
        </v-tooltip>
      </div>
    </template>
    <v-list nav color="transparent" class="app-sidebar__navigation px-3 pt-0">
      <v-menu
        v-for="item in visibleItemList"
        ref="flyoutMenuList"
        :key="item.link"
        :value="isCollapsed && !isMobile && openItemLink === item.link"
        :disabled="!isCollapsed || isMobile || !hasSubmenu(item) || dismissedItemLink === item.link"
        open-on-hover
        offset-x
        transition="sidebar-flyout"
        :close-delay="120"
        content-class="app-sidebar-flyout"
        @input="onMenuInput($event, item)"
      >
        <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
          <div
            class="mb-3"
            v-bind="menuAttrs"
            v-on="menuOn"
            @mouseenter="openSubmenu(item)"
            @mousemove="onPointerMove($event, item)"
            @focusin="onFocusIn($event, item)"
            @focusout="onFocusOut"
            @keydown.esc="closeSubmenu"
          >
            <v-tooltip right content-class="app-sidebar-tooltip" :disabled="!isCollapsed || isMobile || hasSubmenu(item)">
              <template v-slot:activator="{ on, attrs }">
                <div class="app-sidebar__row d-flex align-center" v-bind="attrs" v-on="on">
                  <v-list-item
                    :to="item.link"
                    :aria-label="displayText(item)"
                    class="app-sidebar__item mb-0"
                    :class="{ 'app-sidebar__item--has-submenu': hasSubmenu(item) }"
                    @click="onNavigate(item, $event)"
                  >
                    <v-list-item-icon class="my-3 ml-0 mr-4">
                      <img v-if="item.img" :src="item.img" alt="" width="24" height="24">
                      <v-icon v-else size="24">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content
                      class="app-sidebar__item-content"
                      :aria-hidden="isCollapsed && !isMobile ? 'true' : 'false'"
                    >
                      <span class="app-sidebar__label">{{ displayText(item) }}</span>
                    </v-list-item-content>
                  </v-list-item>
                  <v-btn
                    v-if="hasSubmenu(item)"
                    :aria-hidden="isCollapsed && !isMobile ? 'true' : 'false'"
                    :tabindex="isCollapsed && !isMobile ? -1 : 0"
                    class="app-sidebar__submenu-toggle"
                    icon
                    small
                    :ripple="false"
                    :aria-label="'Submenú de ' + displayText(item)"
                    :aria-expanded="String(openItemLink === item.link)"
                    retain-focus-on-click
                    @click.stop.prevent="toggleSubmenu(item)"
                  >
                    <v-icon small>{{ openItemLink === item.link ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>{{ displayText(item) }}</span>
            </v-tooltip>
            <v-expand-transition>
              <div
                v-if="(!isCollapsed || isMobile) && openItemLink === item.link"
                class="app-sidebar__submenu-container"
              >
                <v-list
                  dense
                  style="background-color: transparent"
                  class="app-sidebar__submenu mt-1 pa-1"
                >
                  <template v-for="child in item.childrenList">
                    <v-list-item v-if="child.shouldShow" :key="child.text" :to="child.link" class="my-1" @click="onNavigate(item, $event)">
                      <v-list-item-icon class="mr-3">
                        <img v-if="child.img" :src="child.img" alt="" width="24" height="24">
                        <v-icon v-else size="24">{{ child.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content><span class="app-sidebar__label">{{ displayText(child) }}</span></v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </div>
            </v-expand-transition>
          </div>
        </template>
        <v-list dark dense class="py-2" :style="flyoutStyle" :aria-label="displayText(item)" @keydown.esc="closeSubmenu">
          <v-subheader>{{ displayText(item) }}</v-subheader>
          <template v-for="child in item.childrenList">
            <v-list-item v-if="child.shouldShow" :key="child.text" :to="child.link" @click="onNavigate(item, $event)">
              <v-list-item-icon class="mr-3 my-0 align-self-center justify-center">
                <img v-if="child.img" :src="child.img" alt="" width="24" height="24">
                <v-icon v-else size="24">{{ child.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>{{ displayText(child) }}</v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'AppSidebar',
  props: {
    value: { type: Boolean, default: true },
    zones: { type: Array, default: () => [] },
    canWrite: { type: Boolean, default: false },
    displayText: { type: Function, required: true },
    background: { type: String, default: 'var(--v-primary-base)' },
    submenuColor: { type: String, default: 'var(--v-primary-base)' },
  },
  data() {
    return {
      isCollapsed: localStorage.getItem('sidebar_collapsed') !== 'false',
      openItemLink: null,
      dismissedItemLink: null,
      dismissedPointerPosition: null,
    };
  },
  computed: {
    flyoutStyle() {
      return {
        background: `color-mix(in srgb, ${this.submenuColor} 60%, black)`,
      };
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    visibleItemList() {
      const itemList = this.zones
        .filter((zone, index) => index < 3 || this.canWrite)
        .reduce((result, zone) => result.concat(zone.items.filter(item => item.shouldShow)), []);
      const childLinkList = itemList.reduce((result, item) => result.concat(
        (item.childrenList || []).filter(child => child.shouldShow).map(child => child.link)
      ), []);
      return itemList.filter(item => !childLinkList.includes(item.link));
    },
  },
  watch: {
    '$route.fullPath': 'closeSubmenu',
    zones: 'closeSubmenu',
  },
  methods: {
    togglePanel() {
      if (this.isMobile) {
        this.$emit('input', false);
        return;
      }
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem('sidebar_collapsed', String(this.isCollapsed));
      this.closeSubmenu();
    },
    hasSubmenu(item) {
      return Boolean(item.childrenList && item.childrenList.some(child => child.shouldShow));
    },
    openSubmenu(item) {
      if (!this.isCollapsed || this.isMobile || this.dismissedItemLink === item.link) return;
      this.openItemLink = this.hasSubmenu(item) ? item.link : null;
    },
    onMenuInput(isOpen, item) {
      if (!this.isCollapsed || this.isMobile) return;
      if (isOpen) this.openSubmenu(item);
      else if (this.openItemLink === item.link) this.closeSubmenu();
    },
    toggleSubmenu(item) {
      this.openItemLink = this.openItemLink === item.link ? null : item.link;
    },
    closeSubmenu() {
      this.openItemLink = null;
    },
    onFocusOut(event) {
      if ((!this.isCollapsed || this.isMobile) && !this.$el.contains(event.relatedTarget)) this.closeSubmenu();
    },
    onFocusIn(event, item) {
      if (!event.target.closest('button')) this.openSubmenu(item);
    },
    onPointerMove(event, item) {
      if (!this.dismissedPointerPosition) return;
      if (event.screenX === this.dismissedPointerPosition.x && event.screenY === this.dismissedPointerPosition.y) return;
      this.dismissedItemLink = null;
      this.dismissedPointerPosition = null;
      this.openSubmenu(item);
    },
    onNavigate(item, event) {
      this.dismissedItemLink = item.link;
      this.dismissedPointerPosition = { x: event.screenX, y: event.screenY };
      // Cancel pending hover activation before resetting both menu states.
      this.$refs.flyoutMenuList.forEach(menu => {
        menu.clearDelay();
        menu.isActive = false;
      });
      this.closeSubmenu();
      if (this.isMobile) this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.app-sidebar {
  background-color: transparent !important;
  color: #F4F4F4;
  transition-property: width, transform, visibility;
  transition-duration: 260ms;
  transition-timing-function: cubic-bezier(0.2, 0.75, 0.25, 1);
}
.app-sidebar__label {
  font-size: 0.875rem;
  white-space: normal;
}
.app-sidebar__row {
  position: relative;
}
.app-sidebar__item {
  min-width: 0;
  padding: 0 12px;
  overflow: hidden;
  transition: padding 260ms cubic-bezier(0.2, 0.75, 0.25, 1);
}
.app-sidebar__item--has-submenu {
  padding-right: 40px;
}
.app-sidebar__item > .v-list-item__icon {
  flex: 0 0 24px;
}
.app-sidebar__item-content {
  flex: 0 0 162px;
  transition: opacity 140ms ease 100ms, visibility 0s;
}
.app-sidebar__item--has-submenu .app-sidebar__item-content {
  flex-basis: 134px;
}
.app-sidebar--collapsed .app-sidebar__item {
  padding-right: 12px;
}
.app-sidebar--collapsed .app-sidebar__item-content {
  opacity: 0;
  visibility: hidden;
  transition: opacity 100ms ease, visibility 0s 100ms;
}
.app-sidebar__submenu-toggle.v-btn {
  position: absolute;
  top: 50%;
  right: 8px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: transparent;
  transform: translateY(-50%);
  transition: opacity 140ms ease 120ms, visibility 0s;
}
.app-sidebar__submenu-toggle.v-btn::before {
  opacity: 0;
}
.app-sidebar__submenu-toggle.v-btn:hover,
.app-sidebar__submenu-toggle.v-btn:focus-visible {
  border-color: #fff;
}
.app-sidebar__submenu-toggle.v-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.app-sidebar--collapsed .app-sidebar__submenu-toggle {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 80ms ease, visibility 0s 80ms;
}
.app-sidebar__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 72px;
  height: 72px;
  padding: 16px 22px;
  box-sizing: border-box;
}
.app-sidebar__navigation {
  min-width: 72px;
  padding-bottom: 96px;
}
.app-sidebar__toggle.v-btn {
  flex: 0 0 28px;
  width: 28px;
  min-width: 28px;
  max-width: 28px;
  height: 28px;
  padding: 0;
  margin: 0;
  text-indent: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  background: transparent;
  color: #bfc0c2;
  transform: translateX(184px);
  transition: transform 260ms cubic-bezier(0.2, 0.75, 0.25, 1), background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}
.app-sidebar__toolbar--collapsed .app-sidebar__toggle {
  transform: translateX(0);
}
.app-sidebar__toggle.v-btn:hover,
.app-sidebar__toggle.v-btn:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.app-sidebar__toggle-icon {
  display: inline-flex;
  transform: rotate(180deg);
  transition: transform 260ms cubic-bezier(0.2, 0.75, 0.25, 1);
}
.app-sidebar__toolbar--collapsed .app-sidebar__toggle-icon {
  transform: rotate(0);
}
.app-sidebar .app-sidebar__submenu-container {
  overflow: hidden;
}
.app-sidebar .app-sidebar__submenu-container.expand-transition-enter-active,
.app-sidebar .app-sidebar__submenu-container.expand-transition-leave-active {
  transition: height 220ms cubic-bezier(0.2, 0.75, 0.25, 1), opacity 140ms ease !important;
}
.app-sidebar__submenu-container.expand-transition-enter,
.app-sidebar__submenu-container.expand-transition-leave-to {
  opacity: 0;
}
.app-sidebar .app-sidebar__submenu {
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}
@media (prefers-reduced-motion: reduce) {
  .app-sidebar .app-sidebar__submenu-container.expand-transition-enter-active,
  .app-sidebar .app-sidebar__submenu-container.expand-transition-leave-active {
    transition: none !important;
  }
}
.app-sidebar__submenu .v-list-item {
  border-radius: 2px;
  transition: background-color 140ms ease;
}
.app-sidebar__submenu .v-list-item:hover,
.app-sidebar__submenu .v-list-item:focus-visible {
  background-color: rgba(255, 255, 255, 0.08);
}
.app-sidebar ::v-deep .v-navigation-drawer__content {
  flex: 1;
  min-height: 0;
  height: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.28) transparent;
}
.app-sidebar ::v-deep .v-navigation-drawer__content::-webkit-scrollbar {
  width: 6px;
  background: transparent;
}
.app-sidebar ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-track {
  background: transparent;
}
.app-sidebar ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.28);
  border-radius: 3px;
}
.app-sidebar ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-button {
  display: none;
}
@media screen and (max-width: 960px) {
  .app-sidebar {
    background: var(--sidebar-background) !important;
  }
}
</style>

<style>
.app-sidebar-tooltip.v-tooltip__content {
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  background: var(--v-primaryDark-base);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  font-size: 12px;
  line-height: 1.4;
}
.app-sidebar ~ .v-main {
  transition-duration: 260ms;
  transition-timing-function: cubic-bezier(0.2, 0.75, 0.25, 1);
}
.app-sidebar-flyout {
  min-width: 208px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  background-color: transparent;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.16);
}
.app-sidebar-flyout .v-list {
  border-radius: inherit;
  padding-right: 6px;
  padding-left: 6px;
  background-color: transparent;
}
.app-sidebar-flyout .v-subheader {
  height: 32px;
  padding: 0 12px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
}
.app-sidebar-flyout .v-list-item {
  border-radius: 2px;
  transition: background-color 140ms ease;
}
.app-sidebar-flyout .v-list-item__icon {
  flex: 0 0 24px;
}
.app-sidebar-flyout .v-list-item:hover,
.app-sidebar-flyout .v-list-item:focus-visible {
  background-color: rgba(255, 255, 255, 0.08);
}
.sidebar-flyout-enter-active,
.sidebar-flyout-leave-active {
  transition: opacity 160ms ease, transform 160ms cubic-bezier(0.2, 0.75, 0.25, 1);
}
.sidebar-flyout-enter,
.sidebar-flyout-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}
@media (prefers-reduced-motion: reduce) {
  .app-sidebar,
  .app-sidebar-tooltip,
  .app-sidebar ~ .v-main,
  .app-sidebar .app-sidebar__toggle,
  .app-sidebar .app-sidebar__toggle-icon,
  .app-sidebar .app-sidebar__submenu-toggle,
  .app-sidebar .app-sidebar__item,
  .app-sidebar .app-sidebar__item-content,
  .app-sidebar .expand-transition-enter-active,
  .app-sidebar .expand-transition-leave-active,
  .sidebar-flyout-enter-active,
  .sidebar-flyout-leave-active {
    transition: none !important;
  }
}
</style>
